import { useState, useMemo, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getConversations, getConversation, getConversationTrace, updateConversationEval, createGoldenAnswer, formatGoldenAnswerPreview, createTestCase, triggerEvalRun, type Conversation } from "@/lib/api";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { GoldenAnswerEditorDialog, SlackPreview } from "@/components/golden-answer";
import { ConfidenceBreakdown, ConfidenceBadge } from "@/components/confidence-breakdown";
import { AgentTrace } from "@/components/agent-trace";
import { WorkflowTraceFlow } from "@/components/workflow-trace";
import { Search, Loader2, Star, MessageSquare, ExternalLink, ThumbsUp, ThumbsDown, Clock, Bot, Sparkles, CheckCircle2, Edit3, MessageSquarePlus, Wand2, Copy, Check, RefreshCw, Cpu, FlaskConical, Play, ChevronLeft, ChevronRight, Ticket } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import type { SlackBlock } from "@/lib/slack-styles";

// Thread group type for organizing conversations
interface ThreadGroup {
  threadKey: string;
  parent: Conversation;
  replies: Conversation[];
}

type FeedbackFilter = '' | 'positive' | 'negative' | 'none';
type ChannelFilter = '' | 'slack' | 'ticket_ai_draft';

const PAGE_SIZE = 20;

export function ConversationsPage() {
  const [search, setSearch] = useState("");
  const [feedbackFilter, setFeedbackFilter] = useState<FeedbackFilter>('');
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>('');
  const [page, setPage] = useState(0);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [expandedThreadId, setExpandedThreadId] = useState<string | null>(null);
  const [evalScore, setEvalScore] = useState<number | null>(null);
  const [editedAnswer, setEditedAnswer] = useState("");
  const [goldenAnswerCreated, setGoldenAnswerCreated] = useState(false);
  const [editorDialogOpen, setEditorDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  // Preview state for Correct Answer
  const [previewBlocks, setPreviewBlocks] = useState<SlackBlock[]>([]);
  const [previewText, setPreviewText] = useState("");
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  // Tab states for Response and Golden Answer sections
  const [responseTab, setResponseTab] = useState<'raw' | 'preview'>('raw');
  const [goldenAnswerTab, setGoldenAnswerTab] = useState<'editor' | 'preview'>('editor');

  // Copy feedback state
  const [copied, setCopied] = useState(false);

  // Conversation detail tab state
  const [detailTab, setDetailTab] = useState<'overview' | 'trace' | 'flow'>('overview');

  // Test case and eval re-run state
  const [testCaseCreated, setTestCaseCreated] = useState(false);
  const [evalRunTriggered, setEvalRunTriggered] = useState(false);

  const handleCopyResponse = async () => {
    if (selectedConversation?.response) {
      await navigator.clipboard.writeText(selectedConversation.response);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["conversations", search, feedbackFilter, channelFilter, page],
    queryFn: () => getConversations({ search, feedback: feedbackFilter, channel: channelFilter, limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
  });

  const totalPages = data?.total ? Math.ceil(data.total / PAGE_SIZE) : 0;

  // Reset page when search or filter changes
  useEffect(() => {
    setPage(0);
  }, [search, feedbackFilter, channelFilter]);

  // Fetch detailed conversation (including messages)
  const { data: detailedConversation } = useQuery({
    queryKey: ["conversation", selectedConversation?.id],
    queryFn: () => selectedConversation ? getConversation(selectedConversation.id) : Promise.resolve(null),
    enabled: !!selectedConversation,
  });

  // Fetch conversation trace to get enriched query for test case creation
  const { data: conversationTrace } = useQuery({
    queryKey: ["conversation-trace", selectedConversation?.id],
    queryFn: () => selectedConversation ? getConversationTrace(selectedConversation.id) : Promise.resolve(null),
    enabled: !!selectedConversation,
  });

  const activeConversation = detailedConversation || selectedConversation;

  const conversations = data?.conversations;
  const { threadGroups, repliesByParent } = useMemo((): { threadGroups: ThreadGroup[]; repliesByParent: Map<string, Conversation[]> } => {
    if (!conversations) {
      return { threadGroups: [], repliesByParent: new Map() };
    }

    const repliesByParent = new Map<string, Conversation[]>();
    const parents: Conversation[] = [];

    for (const conv of conversations) {
      if (conv.isThread && conv.parentConversationId) {
        if (!repliesByParent.has(conv.parentConversationId)) {
          repliesByParent.set(conv.parentConversationId, []);
        }
        repliesByParent.get(conv.parentConversationId)!.push(conv);
      } else {
        parents.push(conv);
      }
    }

    const groups: ThreadGroup[] = parents
      .map((parent) => {
        const replies = (repliesByParent.get(parent.id) || []).sort(
          (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
        return {
          threadKey: parent.id,
          parent,
          replies,
        };
      })
      .sort(
        (a, b) => new Date(b.parent.createdAt).getTime() - new Date(a.parent.createdAt).getTime()
      );

    // Handle orphan replies (missing parent reference)
    for (const conv of conversations) {
      if (conv.isThread && !conv.parentConversationId) {
        groups.push({
          threadKey: conv.id,
          parent: conv,
          replies: [],
        });
      }
    }

    return { threadGroups: groups, repliesByParent };
  }, [conversations]);

  const updateExpandedThread = useCallback((conv: Conversation | null) => {
    if (!conv) {
      setExpandedThreadId(null);
      return;
    }

    const parentKey = conv.isThread ? conv.parentConversationId : conv.id;
    const hasReplies = parentKey ? (repliesByParent.get(parentKey)?.length ?? 0) > 0 : false;

    if (hasReplies && parentKey) {
      setExpandedThreadId(parentKey);
    } else {
      setExpandedThreadId(null);
    }
  }, [repliesByParent]);

  useEffect(() => {
    if (expandedThreadId && !threadGroups.some((group) => group.threadKey === expandedThreadId)) {
      setExpandedThreadId(null);
    }
  }, [expandedThreadId, threadGroups]);

  const evalMutation = useMutation({
    mutationFn: (data: { id: string; evalScore?: number; evalNotes?: string }) =>
      updateConversationEval(data.id, { evalScore: data.evalScore, evalNotes: data.evalNotes }),
    onSuccess: () => {
      // Just invalidate to update the list, don't deselect the conversation
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
  });

  const goldenAnswerMutation = useMutation({
    mutationFn: (data: { conversationId: string; query: string; approvedResponse: string; evalScore: number; isAdminEdited?: boolean }) =>
      createGoldenAnswer(data),
    onSuccess: () => {
      setGoldenAnswerCreated(true);
      queryClient.invalidateQueries({ queryKey: ["golden-answers"] });
    },
  });

  // Create test case from conversation
  const createTestCaseMutation = useMutation({
    mutationFn: (data: { name: string; query: string; description?: string; expectedResponse?: string }) =>
      createTestCase(data),
    onSuccess: () => {
      setTestCaseCreated(true);
      queryClient.invalidateQueries({ queryKey: ["test-cases"] });
    },
  });

  // Re-run conversation as one-time eval (creates a test case first, then runs it)
  const rerunEvalMutation = useMutation({
    mutationFn: async (query: string) => {
      // First create a test case for this query
      const testCaseResult = await createTestCase({
        name: `Ad-hoc: ${query.substring(0, 80)}${query.length > 80 ? '...' : ''}`,
        query,
        description: 'Ad-hoc eval run from conversation',
        category: 'ad-hoc',
      });

      // Then trigger an eval run with just this test case
      return triggerEvalRun(
        `Re-run: ${query.substring(0, 50)}${query.length > 50 ? '...' : ''}`,
        [testCaseResult.id]
      );
    },
    onSuccess: () => {
      setEvalRunTriggered(true);
      queryClient.invalidateQueries({ queryKey: ["eval-runs"] });
      queryClient.invalidateQueries({ queryKey: ["test-cases"] });
    },
  });

  // Fetch preview for Correct Answer
  const fetchPreview = useCallback(async (text: string, query: string) => {
    if (!text.trim()) {
      setPreviewBlocks([]);
      setPreviewText("");
      return;
    }

    setIsPreviewLoading(true);
    try {
      const result = await formatGoldenAnswerPreview({ rawAnswer: text, query });
      setPreviewBlocks(result.blocks);
      setPreviewText(result.formattedText);
    } catch (error) {
      console.error('Preview error:', error);
    } finally {
      setIsPreviewLoading(false);
    }
  }, []);

  // Handle edited answer changes (no auto-formatting)
  const handleEditedAnswerChange = (value: string) => {
    setEditedAnswer(value);
    // Clear AI formatting when text changes - user can re-apply with button
    setPreviewBlocks([]);
    setPreviewText("");
  };

  // Manual AI formatting trigger
  const handleFormatWithAI = () => {
    if (selectedConversation && editedAnswer.trim()) {
      fetchPreview(editedAnswer, selectedConversation.query);
    }
  };

  const handleSelectConversation = (conv: Conversation) => {
    setSelectedConversation(conv);
    setEvalScore(conv.evalScore ?? null);
    // Pre-fill answer with existing response for good scores (4-5), blank for poor scores (1-3)
    const initialAnswer = conv.evalScore && conv.evalScore >= 4 ? conv.response : "";
    setEditedAnswer(initialAnswer);
    setGoldenAnswerCreated(false);
    setTestCaseCreated(false);
    setEvalRunTriggered(false);
    // Reset preview state - user can trigger AI formatting manually
    setPreviewBlocks([]);
    setPreviewText("");

    updateExpandedThread(conv);
  };

  // Update editedAnswer when evalScore changes and auto-save to D1
  const handleEvalScoreChange = (score: number) => {
    setEvalScore(score);
    if (selectedConversation) {
      // Pre-fill with response for good scores, keep blank for poor scores
      const newAnswer = score >= 4 ? selectedConversation.response : "";
      setEditedAnswer(newAnswer);
      // Reset preview - user can trigger AI formatting manually
      setPreviewBlocks([]);
      setPreviewText("");
      // Auto-save eval score to D1 (no explicit "Save" button needed)
      evalMutation.mutate({
        id: selectedConversation.id,
        evalScore: score,
      });
    }
  };

  const handleCreateGoldenAnswer = () => {
    if (selectedConversation && evalScore && editedAnswer.trim()) {
      // Determine if admin edited the answer:
      // - If score < 4, admin is providing a corrected answer
      // - If the edited answer differs from original AI response
      const isAdminEdited = evalScore < 4 || editedAnswer !== selectedConversation.response;

      goldenAnswerMutation.mutate({
        conversationId: selectedConversation.id,
        query: selectedConversation.query,
        approvedResponse: editedAnswer,
        evalScore,
        isAdminEdited,
      });
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Conversations</h1>
            <p className="text-muted-foreground mt-1">
              View and evaluate agent conversations
            </p>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search conversations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Channel filters */}
            <div className="flex gap-1 mr-2 pr-2 border-r">
              <Button
                variant={channelFilter === '' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setChannelFilter('')}
              >
                All Sources
              </Button>
              <Button
                variant={channelFilter === 'slack' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setChannelFilter('slack')}
              >
                <MessageSquare className="h-4 w-4 mr-1" />
                Slack
              </Button>
              <Button
                variant={channelFilter === 'ticket_ai_draft' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setChannelFilter('ticket_ai_draft')}
                className={channelFilter === 'ticket_ai_draft' ? '' : 'text-purple-600 hover:text-purple-700'}
              >
                <Ticket className="h-4 w-4 mr-1" />
                Ticket AI Drafts
              </Button>
            </div>

            {/* Feedback filters */}
            <Button
              variant={feedbackFilter === '' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFeedbackFilter('')}
            >
              All
            </Button>
            <Button
              variant={feedbackFilter === 'positive' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFeedbackFilter('positive')}
              className={feedbackFilter === 'positive' ? '' : 'text-green-600 hover:text-green-700'}
            >
              <ThumbsUp className="h-4 w-4 mr-1" />
              Good
            </Button>
            <Button
              variant={feedbackFilter === 'negative' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFeedbackFilter('negative')}
              className={feedbackFilter === 'negative' ? '' : 'text-red-600 hover:text-red-700'}
            >
              <ThumbsDown className="h-4 w-4 mr-1" />
              Bad
            </Button>
            <Button
              variant={feedbackFilter === 'none' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFeedbackFilter('none')}
            >
              No Feedback
            </Button>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Conversation List - 1/3 width */}
          <Card className="lg:col-span-1">
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2 text-left">
                <MessageSquare className="h-5 w-5" />
                Recent Conversations
              </CardTitle>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => queryClient.invalidateQueries({ queryKey: ["conversations"] })}
                title="Reload conversations"
              >
                <RefreshCw className={cn("h-4 w-4", (isLoading || isFetching) && "animate-spin")} />
              </Button>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {threadGroups.length > 0 ? (
                    threadGroups.map((group) => (
                      <div key={group.threadKey} className="space-y-1">
                        {/* Parent conversation */}
                        <ConversationItem
                          conv={group.parent}
                          isSelected={selectedConversation?.id === group.parent.id}
                          onClick={() => handleSelectConversation(group.parent)}
                          hasReplies={group.replies.length > 0}
                        />

                        {/* Thread replies with indentation */}
                        {expandedThreadId === group.threadKey &&
                          group.replies.map((reply) => (
                            <div key={reply.id} className="ml-4 border-l-2 border-muted pl-2">
                              <ConversationItem
                                conv={reply}
                                isSelected={selectedConversation?.id === reply.id}
                                onClick={() => handleSelectConversation(reply)}
                                isReply
                              />
                            </div>
                          ))}
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No conversations found
                    </p>
                  )}
                </div>
              )}

              {/* Pagination Controls */}
              {!isLoading && totalPages > 0 && (
                <div className="flex items-center justify-between pt-4 border-t mt-4">
                  <span className="text-xs text-muted-foreground">
                    Page {page + 1} of {totalPages} ({data?.total} total)
                  </span>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0 || isFetching}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={page >= totalPages - 1 || isFetching}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Conversation Detail / Eval Panel - 2/3 width */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Bot className="h-5 w-5" />
                  Agent Response
                  {activeConversation && (
                    <span className="text-xs font-normal text-muted-foreground ml-2">
                      ID: {activeConversation.id}
                    </span>
                  )}
                </CardTitle>
                {activeConversation && (
                  <div className="flex gap-1 border rounded-md p-0.5">
                    <button
                      onClick={() => setDetailTab('overview')}
                      className={cn(
                        "px-3 py-1 text-xs rounded transition-colors",
                        detailTab === 'overview'
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      Overview
                    </button>
                    <button
                      onClick={() => setDetailTab('trace')}
                      className={cn(
                        "px-3 py-1 text-xs rounded transition-colors flex items-center gap-1",
                        detailTab === 'trace'
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      <Cpu className="h-3 w-3" />
                      Workflow Trace
                    </button>
                    <button
                      onClick={() => setDetailTab('flow')}
                      className={cn(
                        "px-3 py-1 text-xs rounded transition-colors flex items-center gap-1",
                        detailTab === 'flow'
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      <Sparkles className="h-3 w-3" />
                      Flow
                      <Badge variant="secondary" className="ml-0.5 text-[9px] px-1 py-0">Beta</Badge>
                    </button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {activeConversation ? (
                detailTab === 'trace' ? (
                  <AgentTrace conversationId={activeConversation.id} />
                ) : detailTab === 'flow' ? (
                  <WorkflowTraceFlow conversationId={activeConversation.id} />
                ) : (
                <div className="space-y-4">
                  {activeConversation.messages && activeConversation.messages.length > 0 ? (
                     <div className="space-y-4 mb-4">
                        <label className="text-sm font-medium">Conversation Thread</label>
                        <div className="border rounded-lg p-4 space-y-4 bg-muted/10 max-h-[400px] overflow-y-auto">
                            {activeConversation.messages.map((msg) => (
                                <div key={msg.id} className={cn(
                                    "flex flex-col max-w-[85%]",
                                    msg.role === 'user' ? "ml-auto items-end" : "mr-auto items-start"
                                )}>
                                    <div className={cn(
                                        "px-3 py-2 rounded-lg text-sm shadow-sm",
                                        msg.role === 'user' 
                                            ? "bg-primary text-primary-foreground" 
                                            : "bg-background border"
                                    )}>
                                        <p className="whitespace-pre-wrap">{msg.content}</p>
                                    </div>
                                    <span className="text-[10px] text-muted-foreground mt-1 px-1">
                                        {formatDistanceToNow(new Date(msg.createdAt), { addSuffix: true })}
                                    </span>
                                </div>
                            ))}
                        </div>
                     </div>
                  ) : (
                    <div>
                      <label className="text-sm font-medium">Query</label>
                      <p className="mt-1 text-sm bg-muted p-3 rounded-lg">
                        {activeConversation.query}
                      </p>
                    </div>
                  )}

                  <div>
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium">
                        {activeConversation.messages?.length ? "Latest Response (Evaluation Target)" : "Response"}
                      </label>
                      <div className="flex gap-1 border rounded-md p-0.5">
                        <button
                          onClick={() => setResponseTab('raw')}
                          className={cn(
                            "px-3 py-1 text-xs rounded transition-colors",
                            responseTab === 'raw'
                              ? "bg-primary text-primary-foreground"
                              : "text-muted-foreground hover:text-foreground"
                          )}
                        >
                          Raw
                        </button>
                        <button
                          onClick={() => setResponseTab('preview')}
                          className={cn(
                            "px-3 py-1 text-xs rounded transition-colors",
                            responseTab === 'preview'
                              ? "bg-primary text-primary-foreground"
                              : "text-muted-foreground hover:text-foreground"
                          )}
                        >
                          Preview
                        </button>
                      </div>
                    </div>
                    <div className="mt-2">
                      {responseTab === 'raw' ? (
                        <div className="relative h-72">
                          <textarea
                            readOnly
                            value={activeConversation.response}
                            className="w-full h-full rounded-md border bg-muted/30 p-3 text-xs font-mono resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                          />
                          <button
                            onClick={handleCopyResponse}
                            className="absolute bottom-2 right-2 p-1.5 rounded-md bg-background/80 border shadow-sm hover:bg-muted transition-colors"
                            title="Copy response"
                          >
                            {copied ? (
                              <Check className="h-4 w-4 text-green-500" />
                            ) : (
                              <Copy className="h-4 w-4 text-muted-foreground" />
                            )}
                          </button>
                        </div>
                      ) : (
                        <div className="h-72 overflow-y-auto rounded-md border">
                          <SlackPreview
                            blocks={[]}
                            formattedText={activeConversation.response}
                            botName="OREO"
                            botAvatar="https://avatars.slack-edge.com/2025-11-19/9945428979701_77e4a95a1c49c4962bcc_512.png"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Confidence</label>
                      <div className="text-sm mt-1">
                        <ConfidenceBreakdown
                          confidence={activeConversation.confidence}
                          breakdown={activeConversation.confidenceBreakdown}
                          sources={activeConversation.sources}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium">User Feedback</label>
                      <div className="flex items-center gap-1 mt-1">
                        {activeConversation.feedback === 'positive' && (
                          <>
                            <ThumbsUp className="h-4 w-4 text-green-500" />
                            <span className="text-sm text-green-600">Good</span>
                          </>
                        )}
                        {activeConversation.feedback === 'negative' && (
                          <>
                            <ThumbsDown className="h-4 w-4 text-red-500" />
                            <span className="text-sm text-red-600">Bad</span>
                          </>
                        )}
                        {!activeConversation.feedback && (
                          <span className="text-sm text-muted-foreground">None</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Model</label>
                      <div className="flex items-center gap-1 mt-1">
                        <Bot className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-sm">
                          {activeConversation.model?.split('/').pop() || 'Unknown'}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Response Time</label>
                      <div className="flex items-center gap-1 mt-1">
                        <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-sm">
                          {activeConversation.responseTimeSeconds?.toFixed(1) || '—'}s
                        </span>
                      </div>
                    </div>
                  </div>

                  {activeConversation.sources?.length > 0 && (
                    <div>
                      <label className="text-sm font-medium">Sources</label>
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {activeConversation.sources.map((source) => (
                          <Badge key={source} variant="secondary">
                            {source}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  <hr />

                  {/* Eval Actions - Create Test Case / Re-run */}
                  <div>
                    <label className="text-sm font-medium">Eval Actions</label>
                    <div className="flex gap-2 mt-2">
                      {!testCaseCreated ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => {
                            if (activeConversation) {
                              // Use enriched query from trace if available, otherwise fall back to raw query
                              // This ensures evals use the same search as the original Slack conversation
                              const enrichedQuery = conversationTrace?.phase1?.data.enrichedQuery;
                              const queryForTestCase = enrichedQuery || activeConversation.query;
                              createTestCaseMutation.mutate({
                                name: activeConversation.query.substring(0, 100),
                                query: queryForTestCase,
                                description: enrichedQuery
                                  ? `Created from conversation on ${new Date(activeConversation.createdAt).toLocaleDateString()}. Original query: "${activeConversation.query}"`
                                  : `Created from conversation on ${new Date(activeConversation.createdAt).toLocaleDateString()}`,
                                expectedResponse: activeConversation.response,
                              });
                            }
                          }}
                          disabled={createTestCaseMutation.isPending}
                        >
                          {createTestCaseMutation.isPending ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <FlaskConical className="h-4 w-4 mr-2" />
                          )}
                          Create Test Case
                        </Button>
                      ) : (
                        <div className="flex-1 flex items-center justify-center gap-2 p-2 bg-green-50 rounded-lg border border-green-200 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          <span className="text-green-700">Test case created!</span>
                        </div>
                      )}

                      {!evalRunTriggered ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => {
                            if (activeConversation) {
                              // Use enriched query from trace if available, otherwise fall back to raw query
                              const enrichedQuery = conversationTrace?.phase1?.data.enrichedQuery;
                              const queryForEval = enrichedQuery || activeConversation.query;
                              rerunEvalMutation.mutate(queryForEval);
                            }
                          }}
                          disabled={rerunEvalMutation.isPending}
                        >
                          {rerunEvalMutation.isPending ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Play className="h-4 w-4 mr-2" />
                          )}
                          Re-run as Eval
                        </Button>
                      ) : (
                        <div className="flex-1 flex items-center justify-center gap-2 p-2 bg-blue-50 rounded-lg border border-blue-200 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-blue-600" />
                          <span className="text-blue-700">Eval triggered!</span>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {conversationTrace?.phase1?.data.enrichedQuery && conversationTrace.phase1.data.enrichedQuery !== activeConversation?.query
                        ? 'Test cases will use the enriched query from the original conversation to ensure consistent vector search.'
                        : 'Create a test case to add this query to your eval suite, or re-run it as a one-time eval.'}
                    </p>
                  </div>

                  <hr />

                  <div>
                    <label className="text-sm font-medium">Eval Score (1-5)</label>
                    <div className="flex gap-2 mt-2">
                      {[1, 2, 3, 4, 5].map((score) => (
                        <Button
                          key={score}
                          variant={evalScore === score ? "default" : "outline"}
                          size="sm"
                          onClick={() => handleEvalScoreChange(score)}
                        >
                          {score}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Answer Editor with Preview - shows when eval score is selected */}
                  {evalScore && (
                    <div>
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium flex items-center gap-2">
                            <Edit3 className="h-4 w-4" />
                            {evalScore >= 4 ? "Golden Answer (edit if needed)" : "Correct Answer"}
                          </label>
                          <p className="text-xs text-muted-foreground mt-1">
                            {evalScore >= 4
                              ? "Pre-filled with the AI response. Edit to improve or save as-is."
                              : "Enter the correct answer that should be used for future queries."}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Format with AI button - only shown in preview tab */}
                          {goldenAnswerTab === 'preview' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={handleFormatWithAI}
                              disabled={isPreviewLoading || !editedAnswer.trim()}
                              className="h-7 px-2 text-xs text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                            >
                              <Wand2 className="h-3 w-3 mr-1" />
                              Format with AI
                            </Button>
                          )}
                          <div className="flex gap-1 border rounded-md p-0.5">
                            <button
                              onClick={() => setGoldenAnswerTab('editor')}
                              disabled={isPreviewLoading}
                              className={cn(
                                "px-3 py-1 text-xs rounded transition-colors",
                                goldenAnswerTab === 'editor'
                                  ? "bg-primary text-primary-foreground"
                                  : "text-muted-foreground hover:text-foreground",
                                isPreviewLoading && "opacity-50 cursor-not-allowed"
                              )}
                            >
                              Editor
                            </button>
                            <button
                              onClick={() => setGoldenAnswerTab('preview')}
                              className={cn(
                                "px-3 py-1 text-xs rounded transition-colors min-w-[60px]",
                                goldenAnswerTab === 'preview'
                                  ? "bg-primary text-primary-foreground"
                                  : "text-muted-foreground hover:text-foreground"
                              )}
                            >
                              {isPreviewLoading ? (
                                <Loader2 className="h-3 w-3 animate-spin mx-auto" />
                              ) : (
                                "Preview"
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="mt-2">
                        {goldenAnswerTab === 'editor' ? (
                          <textarea
                            value={editedAnswer}
                            onChange={(e) => handleEditedAnswerChange(e.target.value)}
                            placeholder={evalScore >= 4
                              ? "Response will be saved as the golden answer..."
                              : "Enter the correct answer here..."}
                            className="w-full h-72 rounded-md border bg-muted/30 px-3 py-2 text-xs font-mono resize-none"
                          />
                        ) : (
                          <div className="h-72 overflow-y-auto rounded-md border">
                            <SlackPreview
                              blocks={previewBlocks}
                              formattedText={previewText || editedAnswer}
                              isLoading={isPreviewLoading}
                              botName="OREO"
                              botAvatar="https://avatars.slack-edge.com/2025-11-19/9945428979701_77e4a95a1c49c4962bcc_512.png"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Golden Answer Buttons */}
                  {evalScore && !goldenAnswerCreated && (
                    <div className="flex gap-2">
                      {/* Save as Golden Answer - saves the inline edited answer */}
                      <Button
                        onClick={handleCreateGoldenAnswer}
                        disabled={goldenAnswerMutation.isPending || !editedAnswer.trim() || isPreviewLoading}
                        variant="outline"
                        className="flex-1 border-yellow-500 text-yellow-600 hover:bg-yellow-50 hover:text-yellow-700"
                      >
                        {goldenAnswerMutation.isPending ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Sparkles className="mr-2 h-4 w-4" />
                        )}
                        Save as Golden Answer
                      </Button>

                      {/* Make Changes - opens advanced editor with Slack preview */}
                      <Button
                        onClick={() => setEditorDialogOpen(true)}
                        disabled={isPreviewLoading}
                        variant="outline"
                        className="flex-1 border-blue-500 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                      >
                        <Edit3 className="mr-2 h-4 w-4" />
                        Advanced Editor
                      </Button>
                    </div>
                  )}

                  {goldenAnswerCreated && (
                    <div className="flex items-center justify-center gap-2 p-2 bg-green-50 rounded-lg border border-green-200">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-green-700">Golden answer created! Future queries will use this response.</span>
                    </div>
                  )}

                  {/* View in Slack or View Ticket button */}
                  {isTicketAIDraft(activeConversation) && activeConversation.ticketId ? (
                    <Button variant="outline" className="w-full gap-2" asChild>
                      <a href={`/tickets?ticketId=${activeConversation.ticketId}`}>
                        <Ticket className="h-4 w-4" />
                        View Ticket
                      </a>
                    </Button>
                  ) : activeConversation.channel && (
                    <Button variant="outline" className="w-full gap-2" asChild>
                      <a
                        href={`https://app.slack.com/client/T01JBRY0Z19/${activeConversation.channel}/thread/${activeConversation.threadTs}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="h-4 w-4" />
                        View in Slack
                      </a>
                    </Button>
                  )}
                </div>
                )
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  Select a conversation to evaluate
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Golden Answer Editor Dialog */}
      {selectedConversation && evalScore && (
        <GoldenAnswerEditorDialog
          open={editorDialogOpen}
          onOpenChange={setEditorDialogOpen}
          conversation={selectedConversation}
          initialScore={evalScore}
          onSuccess={() => {
            setGoldenAnswerCreated(true);
            queryClient.invalidateQueries({ queryKey: ["golden-answers"] });
          }}
        />
      )}
    </Layout>
  );
}

// Conversation item component for thread display
interface ConversationItemProps {
  conv: Conversation;
  isSelected: boolean;
  onClick: () => void;
  hasReplies?: boolean;
  isReply?: boolean;
}

// Check if conversation is a ticket AI draft
function isTicketAIDraft(conv: Conversation): boolean {
  return conv.metadata?.channel === 'ticket_ai_draft' || !!conv.ticketId;
}

function ConversationItem({
  conv,
  isSelected,
  onClick,
  hasReplies = false,
  isReply = false,
}: ConversationItemProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full text-left p-3 rounded-lg border transition-colors",
        isSelected
          ? "border-primary bg-primary/5"
          : "hover:bg-accent",
        isReply && "py-2" // Slightly smaller padding for replies
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          {isReply && (
            <MessageSquarePlus className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
          )}
          <p className={cn(
            "font-medium line-clamp-2",
            isReply ? "text-xs" : "text-sm"
          )}>
            {conv.query}
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {hasReplies && (
            <Badge variant="secondary" className="text-xs px-1.5">
              +{/* replies count would go here if we had it */}
            </Badge>
          )}
          {conv.feedback === 'positive' && (
            <ThumbsUp className="h-3.5 w-3.5 text-green-500" />
          )}
          {conv.feedback === 'negative' && (
            <ThumbsDown className="h-3.5 w-3.5 text-red-500" />
          )}
          {conv.evalScore !== undefined && (
            <>
              <Star className="h-3 w-3 fill-yellow-500 text-yellow-500" />
              <span className="text-xs">{conv.evalScore}</span>
            </>
          )}
        </div>
      </div>
      <div className={cn(
        "flex items-center gap-2 mt-2 flex-wrap",
        isReply && "mt-1"
      )}>
        {isTicketAIDraft(conv) ? (
          <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
            <Ticket className="h-3 w-3 mr-1" />
            Ticket AI
          </Badge>
        ) : (
          <ConfidenceBadge confidence={conv.confidence} breakdown={conv.confidenceBreakdown} sources={conv.sources} />
        )}
        {conv.model && !isReply && !isTicketAIDraft(conv) && (
          <Badge variant="secondary" className="text-xs">
            {conv.model.split('/').pop()}
          </Badge>
        )}
        {conv.responseTimeSeconds != null && !isReply && (
          <span className="text-xs text-muted-foreground flex items-center gap-0.5">
            <Clock className="h-3 w-3" />
            {conv.responseTimeSeconds.toFixed(1)}s
          </span>
        )}
        <span className="text-xs text-muted-foreground">
          {conv.displayName && `${conv.displayName}, `}{formatDistanceToNow(new Date(conv.createdAt), { addSuffix: true })}
        </span>
      </div>
    </button>
  );
}
